import React, { useMemo, useState } from 'react';

import { ExpandSmall } from '@rover/icons';
import { Box, Button, Flex, Heading, Paragraph, Text } from '@rover/kibble/core';
import { DSTokenMap } from '@rover/kibble/styles';
import { HeadingSizes } from '@rover/kibble/tokens/build/rover/es6/theme';

import TrainingMultiSelectPills from '../TrainingMultiSelectPills';

import {
  CURRENT_BEHAVIOR_SLUGS_TO_LABELS,
  CURRENT_SKILL_SLUGS_TO_LABELS,
  ORDERED_TRAINING_BEHAVIOR_LABELS_TO_SLUGS_MAP,
  ORDERED_TRAINING_SKILL_LABELS_TO_SLUGS_MAP,
} from './constants';

export type TrainingNeedsPillsProps = {
  title: string;
  subtitle: string;
  startCollapsed?: boolean;
  behaviorsOnChange: (arg0: string[]) => void;
  skillsOnChange: (arg0: string[]) => void;
  selectedBehaviors: string[];
  selectedSkills: string[];
  titleSize?: HeadingSizes;
};

export const TrainingNeedsPills = ({
  title,
  subtitle,
  startCollapsed = false,
  behaviorsOnChange,
  skillsOnChange,
  selectedBehaviors,
  selectedSkills,
  titleSize = '200',
}: TrainingNeedsPillsProps): JSX.Element => {
  const [collapsed, setCollapsed] = useState<boolean>(startCollapsed);

  const behaviorOptions = useMemo(() => {
    const allBehaviorOptions = Array.from(ORDERED_TRAINING_BEHAVIOR_LABELS_TO_SLUGS_MAP.keys());
    if (collapsed) {
      return allBehaviorOptions.splice(0, 2);
    }
    return allBehaviorOptions;
  }, [collapsed]);

  return (
    <Box>
      <Heading as="h3" size={titleSize} responsive={false} paddingTop="2x" marginBottom="1x">
        {title}
      </Heading>
      <Paragraph size="100" marginBottom="3x" textColor="tertiary" sx={{ lineHeight: 1.5 }}>
        {subtitle}
      </Paragraph>
      <Paragraph size="100" marginBottom="2x" textColor="tertiary" fontWeight="semibold">
        Training types
      </Paragraph>
      <TrainingMultiSelectPills
        options={Array.from(ORDERED_TRAINING_SKILL_LABELS_TO_SLUGS_MAP.keys())}
        values={selectedSkills.map((skill) => CURRENT_SKILL_SLUGS_TO_LABELS[skill])}
        onChange={(selections) =>
          skillsOnChange(
            selections.map((skill) => ORDERED_TRAINING_SKILL_LABELS_TO_SLUGS_MAP.get(skill) || '')
          )
        }
        variant="small"
      />
      <Paragraph
        size="100"
        paddingTop="4x"
        marginBottom="2x"
        textColor="tertiary"
        fontWeight="semibold"
      >
        Behaviors
      </Paragraph>
      <TrainingMultiSelectPills
        options={behaviorOptions}
        values={selectedBehaviors.map((behavior) => CURRENT_BEHAVIOR_SLUGS_TO_LABELS[behavior])}
        onChange={(selections) =>
          behaviorsOnChange(
            selections.map(
              (behavior) => ORDERED_TRAINING_BEHAVIOR_LABELS_TO_SLUGS_MAP.get(behavior) || ''
            )
          )
        }
        variant="small"
      />
      {collapsed && (
        <Button
          onClick={() => setCollapsed(false)}
          variant="flat"
          paddingX="4x"
          paddingY="2x"
          width="100%"
          sx={{
            '&:hover, &:focus': { background: 'none' },
          }}
        >
          <Flex gap="2x" alignItems="center">
            <Text size="100">
              See more behaviors ({ORDERED_TRAINING_BEHAVIOR_LABELS_TO_SLUGS_MAP.size - 2})
            </Text>
            <ExpandSmall
              width="16px"
              height="16px"
              fill={DSTokenMap.INTERACTIVE_LINK_COLOR_PRIMARY.toString()}
            />
          </Flex>
        </Button>
      )}
    </Box>
  );
};
